/**
 * Custom element for handling cart remove button functionality
 * @class CartRemoveButton
 * @extends HTMLElement
 */
class CartRemoveButton extends HTMLElement {
  /**
   * Creates an instance of CartRemoveButton
   * @constructor
   */
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

/**
 * Custom element for handling cart items functionality
 * @class CartItems
 * @extends HTMLElement
 */
class CartItems extends HTMLElement {
  /**
   * Creates an instance of CartItems
   * @constructor
   */
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  /**
   * Called when the element is connected to the DOM
   */
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === 'cart-items') {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  /**
   * Called when the element is disconnected from the DOM
   */
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  /**
   * Handles change events on cart item inputs
   * @param {Event} event - The change event
   */
  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  /**
   * Handles cart update events
   */
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Gets the sections that need to be rendered
   * @returns {Array} Array of section objects
   */
  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Updates the quantity of a cart item
   * @param {number} line - The line item index
   * @param {number} quantity - The new quantity
   * @param {string} name - The name attribute of the input
   */
  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    this.querySelectorAll('.quantity__button').forEach((button) =>
      button.classList.add('disabled')
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        parsedState.items.forEach((item) => {
          const productCard = document.querySelector(
            "[data-variant-id='" + item.variant_id + "']"
          );
          if (productCard == null || productCard == undefined) return;
          productCard.querySelector('.quantity_input')
            ? (productCard.querySelector('.quantity_input').value =
                item.quantity)
            : null;
        });

        if (quantity == 0) {
          parsedState.items_removed.forEach((item) => {
            const productCard = document.querySelector(
              "[data-variant-id='" + item.variant_id + "']"
            );
            if (productCard == null || productCard == undefined) return;
            productCard.querySelector('.buy-button')
              ? productCard.querySelector('.buy-button').classList.add('active')
              : null;
            productCard.querySelector('.change-button')
              ? productCard
                  .querySelector('.change-button')
                  .classList.remove('active')
              : null;
            productCard.querySelector('.quantity_input')
              ? (productCard.querySelector('.quantity_input').value = 0)
              : null;
          });
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter)
          cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              '[quantity]',
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper.querySelector('a')
          );
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector('.cart-item__name')
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

        // For refactoring -- Totally create a new copy of cart.js
        window.refreshCartAccordion && window.refreshCartAccordion();
      })
      .catch((err) => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden')
        );
        this.querySelectorAll('.quantity__button').forEach((button) =>
          button.classList.remove('disabled')
        );
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
        console.log(err);
      })
      .finally(() => {
        this.querySelectorAll('.quantity__button').forEach((button) =>
          button.classList.remove('disabled')
        );
        this.disableLoading(line);
      });
  }

  /**
   * Updates live regions for accessibility
   * @param {number} line - The line item index
   * @param {string} message - The message to display
   */
  updateLiveRegions(line, message) {
    discountApply();
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * Gets the inner HTML of a section
   * @param {string} html - The HTML content
   * @param {string} selector - The selector to find the section
   * @returns {string} The inner HTML of the section
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  /**
   * Enables loading state for a cart item
   * @param {number} line - The line item index
   */
  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  /**
   * Disables loading state for a cart item
   * @param {number} line - The line item index
   */
  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add('hidden')
    );
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('shipping-window-lookup')) {
  /**
   * Custom element for handling shipping window lookup functionality
   * @class ShippingWindowLookup
   * @extends HTMLElement
   */
  class ShippingWindowLookup extends HTMLElement {
    /**
     * Creates an instance of ShippingWindowLookup
     * @constructor
     */
    constructor() {
      super();

      this.isEmbeddedInDeliveryPage = this.dataset.isPage;
      this.existingDeliveryLocation = null;
      this.existingFirstDeliveryWindow = null;

      console.log(
        'cdebug this.existingDeliveryLocation',
        this.existingDeliveryLocation
      );
      console.log(
        'cdebug this.existingFirstDeliveryWindow',
        this.existingFirstDeliveryWindow
      );

      this.resultsToggleBtn = this.querySelector('.btn--delivery-info');
      this.resultsContainer = this.querySelector('.results-container');
      this.searchInputContainer = this.querySelector('.form-control');
      this.searchInput = this.querySelector('input');
      this.resultsContainer = this.querySelector('.results-container');
      this.autocompleteResultsContainer = this.querySelector(
        '.autocomplete-results'
      );
      this.changePostcodeBtn = this.querySelector('.change-postcode-btn');
      this.errorMessage = this.querySelector('.delivery-info__msg--error');
      this.deliveryInfoMessage = this.querySelector('.delivery-info__msg');
      this.deliveryInfoWindowMessage = this.querySelector(
        '.delivery-info__delivery-window-msg'
      );

      // Embedded page controls
      this.deliveryInfoBody = document.querySelector('.delivery-info_body');
      this.deliveryInfoMessagePage = document.querySelector(
        '.delivery-info_body .delivery-info_msg'
      );
      this.nextDeliveryInfoContainer = document.querySelector(
        '.delivery-info--next .info-item_list'
      );
      this.deliveryScheduleContainer = document.querySelector(
        '.delivery-info--schedule .delivery-info_info'
      );
    }

    /**
     * Hides or shows the delivery info body
     * @param {boolean} hide - Whether to hide the delivery info body
     */
    hideDeliveryInfoBody(hide) {
      if (hide) {
        this.deliveryInfoBody.classList.add('hidden');
      } else {
        this.deliveryInfoBody.classList.remove('hidden');
      }
    }

    /**
     * Displays shipping window information
     * @param {Object} firstDeliveryWindow - The first delivery window object
     * @param {Object} selectedPostcodeSuburb - The selected postcode and suburb
     * @param {Date} now - The current date
     */
    displayShippingWindowInformation(
      firstDeliveryWindow,
      selectedPostcodeSuburb,
      now
    ) {
      let {
        nextDeliveryDate,
        orderCutoffDate,
        orderCutoffDateToday,
        cutoffTimeAP,
      } = getDeliveryAndCutoffDates(firstDeliveryWindow, now);
      let formattedNextDeliveryDate = formatDatePretty(nextDeliveryDate);
      let formattedOrderCutoffDate = formatDatePretty(orderCutoffDate);

      console.log('nextDeliveryDate', nextDeliveryDate);
      console.log('orderCutoffDate', orderCutoffDate);
      console.log('orderCutoffDateToday', orderCutoffDateToday);
      console.log('formattedNextDeliveryDate', formattedNextDeliveryDate);

      // this.deliveryInfoMessage.innerHTML = `<p class="text-center">Yes! Here's the delivery information for  <span class="suburb">${selectedPostcodeSuburb.suburb} (${selectedPostcodeSuburb.postcode})</span></p>`

      this.deliveryInfoMessage.innerHTML =
        window.shippingWindowsCopy.deliveryInfoMessage
          .replace('{suburb}', selectedPostcodeSuburb.suburb)
          .replace('{postcode}', selectedPostcodeSuburb.postcode);

      // this.deliveryInfoWindowMessage.innerHTML = `<p class="text-center">Next delivery on ${formattedNextDeliveryDate}, order ${orderCutoffDateToday ? "today": ""} before ${!orderCutoffDateToday ? formattedOrderCutoffDate : ""} ${cutoffTimeAP}</p>`
      if (orderCutoffDateToday) {
        this.deliveryInfoWindowMessage.innerHTML =
          window.shippingWindowsCopy.deliveryFirstScheduleAlt
            .replace('{deliveryDate}', formattedNextDeliveryDate)
            .replace('{formattedOrderCutoffDate}', formattedOrderCutoffDate)
            .replace('{cutoffTimeAP}', cutoffTimeAP);
      } else {
        this.deliveryInfoWindowMessage.innerHTML =
          window.shippingWindowsCopy.deliveryFirstSchedule
            .replace('{deliveryDate}', formattedNextDeliveryDate)
            .replace('{formattedOrderCutoffDate}', formattedOrderCutoffDate)
            .replace('{cutoffTimeAP}', cutoffTimeAP);
      }

      this.deliveryInfoMessage.classList.remove('hidden');
      this.deliveryInfoWindowMessage.classList.remove('hidden');
    }

    /**
     * Displays the delivery schedule for a zone
     * @param {Array} zoneDeliverySchedules - The delivery schedules for the zone
     */
    displayZoneDeliverySchedule(zoneDeliverySchedules) {
      let output = ``;

      for (let schedule of zoneDeliverySchedules) {
        let { timeStartAP, timeEndAP } = getFormattedTimes(schedule);
        output += `<div class="info-item_list">
								<div class="item_top">${schedule.weekdayTitle}</div>
								<div class="item_inner">${timeStartAP} - ${timeEndAP}</div>
							</div>`;
      }

      this.deliveryScheduleContainer.innerHTML = output;
    }

    /**
     * Displays shipping window info for the page
     * @param {Object} firstDeliveryWindow - The first delivery window object
     * @param {Object} selectedPostcodeSuburb - The selected postcode and suburb
     * @param {Date} now - The current date
     */
    displayShippingWindowInfoForPage(
      firstDeliveryWindow,
      selectedPostcodeSuburb,
      now
    ) {
      let {
        nextDeliveryDate,
        orderCutoffDate,
        orderCutoffDateToday,
        cutoffTimeAP,
      } = getDeliveryAndCutoffDates(firstDeliveryWindow, now);
      let formattedNextDeliveryDate =
        formatDatePrettyWithYear(nextDeliveryDate);
      let { timeStartAP, timeEndAP } = getFormattedTimes(firstDeliveryWindow);

      console.log('cdebug', selectedPostcodeSuburb);

      // this.deliveryInfoMessagePage.innerHTML = `<p class="text-center">Yes! Here's the delivery information for  <span class="suburb">${selectedPostcodeSuburb.suburb} (${selectedPostcodeSuburb.postcode})</span></p>`;

      this.deliveryInfoMessagePage.innerHTML =
        window.shippingWindowsCopy.deliveryInfoMessage
          .replace('{suburb}', selectedPostcodeSuburb.suburb)
          .replace('{postcode}', selectedPostcodeSuburb.postcode);

      this.nextDeliveryInfoContainer.innerHTML = `<div class="item_top">${formattedNextDeliveryDate}</div><div class="item_inner">${timeStartAP} - ${timeEndAP}</div>`;
    }

    /**
     * Handles the results toggle button click
     * @param {Event} e - The click event
     */
    handleResultsToggleClick(e) {
      this.resultsContainer.classList.toggle('hidden');

      // For now, focus right away
      if (!this.resultsContainer.classList.contains('hidden')) {
        this.searchInput && this.searchInput.focus();
      }
    }

    /**
     * Handles input field changes
     * @param {Event} e - The input event
     */
    handleInputFieldChange(e) {
      const searchTerm = e.target.value;

      const search = (results, term) => {
        if (isNaN(term)) {
          return results
            .filter((item) => {
              const name = `${item.suburb} ${item.postcode}`;
              return name.toLowerCase().startsWith(term.toLowerCase());
            })
            .slice(0, 10);
        }
        return results
          .filter((item) => {
            return item?.postcode?.startsWith(term);
          })
          .slice(0, 10);
      };

      var filtered = search(window.shippingDataZones, `${searchTerm}`);

      const generateResultsElements = (filtered) => {
        let results = '';
        filtered.forEach(function (item) {
          results += `<li class="autocomplete-result">
								<button class="autocomplete-result-btn" data-name="${item.suburb} ${item.postcode}"
								data-suburb="${item.suburb}" data-postcode="${item.postcode}" data-zone="${item.zone}">${item.suburb} ${item.zone} ${item.postcode}</button>
							  </li>
							`;
        });

        return results;
      };

      this.autocompleteResultsContainer.innerHTML = '';

      const resultElements = generateResultsElements(filtered);
      if (searchTerm && resultElements) {
        this.autocompleteResultsContainer.innerHTML = resultElements;
      }

      if (filtered.length == 0) {
        this.errorMessage.classList.remove('hidden');
        if (this.isEmbeddedInDeliveryPage) {
          this.hideDeliveryInfoBody(true);
        }
      } else {
        this.errorMessage.classList.add('hidden');
      }
    }

    /**
     * Renders the results for a given delivery location
     * @param {Object} incomingDeliveryLocation - The delivery location object
     * @param {Object} [incomingFirstShippingWindow=null] - The first shipping window object
     */
    renderResults(
      incomingDeliveryLocation,
      incomingFirstShippingWindow = null
    ) {
      console.log(
        'cdebug renderResults...',
        incomingDeliveryLocation,
        incomingFirstShippingWindow
      );
      const { zone, suburb, postcode } = incomingDeliveryLocation;

      this.changePostcodeBtn.classList.remove('hidden');
      this.searchInputContainer.classList.add('hidden');

      this.autocompleteResultsContainer.innerHTML = '';

      // Check first if deliveryZone and delivery windows are available
      let shippingZoneIsAvailable = incomingFirstShippingWindow
        ? true
        : isShippingZoneAvailable(zone);
      let firstDeliveryWindow = {};
      let now = new Date();
      if (window.overrideDate) {
        now = new Date(window.overrideDate);
      }
      let selectedPostcodeSuburb = { suburb, postcode, zone };
      let deliveryZoneDeliveryDates = [];

      console.log('cdebug shippingZoneIsAvailable', shippingZoneIsAvailable);

      // If deliveryZone is available, save the data and print out the information
      if (shippingZoneIsAvailable) {
        setPostcodeSuburbAsCartAttribute({
          postcode,
          suburb,
        });
        saveToLocalStorage('delivery_location', selectedPostcodeSuburb);
        firstDeliveryWindow = incomingFirstShippingWindow
          ? incomingFirstShippingWindow
          : findFirstDeliveryWindow(now, zone);

        if (firstDeliveryWindow) {
          console.log('cdebug firstDeliveryWindow', firstDeliveryWindow);
          saveToLocalStorage('first_delivery_window', firstDeliveryWindow);

          console.log('cdebug', window.shippingWindows);

          if (this.isEmbeddedInDeliveryPage) {
            deliveryZoneDeliveryDates = sortByWeekdayId(
              findZoneDeliveryDates(now, zone)
            );

            this.deliveryInfoMessage.classList.add('hidden');
            this.deliveryInfoWindowMessage.classList.add('hidden');

            this.hideDeliveryInfoBody(false);
            this.displayShippingWindowInfoForPage(
              firstDeliveryWindow,
              selectedPostcodeSuburb,
              now
            );
            this.displayZoneDeliverySchedule(deliveryZoneDeliveryDates);
          } else {
            this.displayShippingWindowInformation(
              firstDeliveryWindow,
              selectedPostcodeSuburb,
              now
            );
          }
        } else {
          this.errorMessage.classList.remove('hidden');
          if (this.isEmbeddedInDeliveryPage) {
            this.hideDeliveryInfoBody(true);
          }
        }
      } else {
        // If deliveryZone is not available, show error message
        this.errorMessage.classList.remove('hidden');
      }
    }

    /**
     * Handles autocomplete results click
     * @param {Event} e - The click event
     */
    handleAutocompleteResultsClick(e) {
      const button = e.target.closest('button');
      const suburb = button.dataset.suburb;
      const postcode = button.dataset.postcode;
      const zone = button.dataset.zone;

      this.renderResults({
        suburb,
        postcode,
        zone,
      });
    }

    /**
     * Handles the change postcode button click
     * @param {Event} e - The click event
     */
    handleChangeBtnClick(e) {
      this.searchInputContainer.classList.remove('hidden');
      this.changePostcodeBtn.classList.add('hidden');
      this.searchInput.value = '';
      this.searchInput && this.searchInput.focus();

      if (this.isEmbeddedInDeliveryPage) {
        this.hideDeliveryInfoBody(true);
      } else {
        this.deliveryInfoMessage.classList.add('hidden');
        this.deliveryInfoWindowMessage.classList.add('hidden');
        this.errorMessage.classList.add('hidden');
        this.deliveryInfoMessage.innerHTML = '';
        this.deliveryInfoWindowMessage.innerHTML = '';
      }
    }

    /**
     * Called when the element is connected to the DOM
     */
    connectedCallback() {
      this.existingDeliveryLocation = getFromLocalStorage('delivery_location');
      this.existingFirstDeliveryWindow = getFromLocalStorage(
        'first_delivery_window'
      );

      console.log(
        'cdebug this.existingDeliveryLocation',
        this.existingDeliveryLocation
      );
      console.log(
        'cdebug this.existingFirstDeliveryWindow',
        this.existingFirstDeliveryWindow
      );

      const onResultsToggleClick = this.handleResultsToggleClick.bind(this);
      const onInputChange = this.handleInputFieldChange.bind(this);
      const onAutocompleteResultsClick =
        this.handleAutocompleteResultsClick.bind(this);
      const onHandleChangeBtnClick = this.handleChangeBtnClick.bind(this);

      // Initialize any necessary elements or event listeners when the element is added to the DOM
      this.resultsToggleBtn.addEventListener('click', onResultsToggleClick);
      this.searchInput.addEventListener('keyup', onInputChange);
      this.autocompleteResultsContainer.addEventListener(
        'click',
        onAutocompleteResultsClick
      );
      this.changePostcodeBtn.addEventListener('click', onHandleChangeBtnClick);

      if (this.existingDeliveryLocation && this.existingFirstDeliveryWindow) {
        this.resultsContainer.classList.remove('hidden');
        this.renderResults(
          this.existingDeliveryLocation,
          this.existingFirstDeliveryWindow
        );
      }
    }

    /**
     * Called when shipping windows are loaded
     * @param {Object} data - The shipping windows data
     */
    onShippingWindowsLoaded(data) {
      console.log('cdebug onShippingWindowsLoaded', data);
      let firstDeliveryWindow = {};
      let now = new Date();
      if (window.overrideDate) {
        now = new Date(window.overrideDate);
      }

      if (this.existingDeliveryLocation && this.existingFirstDeliveryWindow) {
        let { zone } = this.existingDeliveryLocation;
        this.renderResults(
          this.existingDeliveryLocation,
          this.existingFirstDeliveryWindow
        );

        // Double check delivery window data to make sure correctness as data from localstorage might be stale.
        firstDeliveryWindow = findFirstDeliveryWindow(now, zone);
        this.renderResults(this.existingDeliveryLocation, firstDeliveryWindow);
      }
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    disconnectedCallback() {
      // Clean up any event listeners or resources when the element is removed from the DOM
      this.resultsToggleBtn.removeEventListener('click');
      this.searchInput.removeEventListener('keyup');
    }
  }

  customElements.define('shipping-window-lookup', ShippingWindowLookup);
}
